import { supabase } from '../client'
import type { CloudCanvasNote } from '../types'
import { logSupabaseError } from './logSupabaseError'

/* ── CANVAS NOTES (Projekt-Canvas: Notizen & Memos) ─────────────────────────────────────────── */

export async function getCloudCanvasNotes(userId: string, since?: Date): Promise<CloudCanvasNote[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('canvas_notes')
      .select('*')
      .eq('user_id', userId)
      .is('deleted_at', null)
    if (since) query = query.gte('updated_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get canvas notes error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get canvas notes error', error)
    return []
  }
}

export async function getCloudCanvasNoteIds(userId: string): Promise<string[]> {
  if (!supabase) return []
  try {
    const { data, error } = await supabase
      .from('canvas_notes')
      .select('id')
      .eq('user_id', userId)
      .is('deleted_at', null)
      .limit(100000)
    if (error) {
      logSupabaseError('Get canvas note IDs error', error)
      return []
    }
    return (data || []).map(row => row.id)
  } catch (error) {
    logSupabaseError('Get canvas note IDs error', error)
    return []
  }
}

export async function getCloudCanvasNotesByIds(userId: string, ids: string[]): Promise<CloudCanvasNote[]> {
  if (!supabase || ids.length === 0) return []
  try {
    const { data, error } = await supabase
      .from('canvas_notes')
      .select('*')
      .eq('user_id', userId)
      .in('id', ids)
      .is('deleted_at', null)
    if (error) {
      logSupabaseError('Get canvas notes by IDs error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get canvas notes by IDs error', error)
    return []
  }
}

export async function getDeletedCloudCanvasNotes(userId: string, since?: Date): Promise<{ id: string; local_id: string | null; updated_at: string }[]> {
  if (!supabase) return []
  try {
    let query = supabase
      .from('canvas_notes')
      .select('id, local_id, updated_at')
      .eq('user_id', userId)
      .not('deleted_at', 'is', null)
    if (since) query = query.gte('deleted_at', since.toISOString())
    const { data, error } = await query
    if (error) {
      logSupabaseError('Get deleted canvas notes error', error)
      return []
    }
    return data || []
  } catch (error) {
    logSupabaseError('Get deleted canvas notes error', error)
    return []
  }
}

export async function createCloudCanvasNote(
  userId: string,
  note: Omit<CloudCanvasNote, 'id' | 'user_id' | 'created_at' | 'updated_at'>,
): Promise<CloudCanvasNote | null> {
  if (!supabase) return null
  try {
    const { data, error } = await supabase
      .from('canvas_notes')
      .insert({ ...note, user_id: userId })
      .select()
      .single()
    if (error) {
      logSupabaseError('Create canvas note error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Create canvas note error', error)
    return null
  }
}

export async function updateCloudCanvasNote(
  noteId: string,
  updates: Partial<Omit<CloudCanvasNote, 'id' | 'user_id' | 'created_at'>>,
  userId?: string,
): Promise<{ updated_at: string } | null> {
  if (!supabase) return null
  try {
    let query = supabase
      .from('canvas_notes')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', noteId)
    if (userId) query = query.eq('user_id', userId)
    const { data, error } = await query.select('updated_at').single()
    if (error) {
      logSupabaseError('Update canvas note error', error)
      return null
    }
    return data
  } catch (error) {
    logSupabaseError('Update canvas note error', error)
    return null
  }
}

export async function deleteCloudCanvasNote(noteId: string, userId?: string): Promise<boolean> {
  if (!supabase) return false
  try {
    let query = supabase
      .from('canvas_notes')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', noteId)
    if (userId) query = query.eq('user_id', userId)
    const { error } = await query
    if (error) {
      logSupabaseError('Delete canvas note error', error)
      return false
    }
    return true
  } catch (error) {
    logSupabaseError('Delete canvas note error', error)
    return false
  }
}
